import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Ritual } from '../../@types/Calculations';
import { SimState, StateCalculation } from '../../@types/State';
import { draftRates, rituals } from '../../helpers/GameAgeData';
import { getGameState } from '../../helpers/SimStateCalculator';
const maxDuration = 120;

const getResult = (result: Result) => {
  return (
    <TableRow
      key={result.tick}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component='th' scope='row'>
        {result.tick}
      </TableCell>
      <TableCell align='right'>{result.netIncome}</TableCell>
      <TableCell align='right'>{result.soldiersDafted}</TableCell>
      <TableCell align='right'>{result.currentSoldiers}</TableCell>
      <TableCell align='right'>{result.totalGcsEarned}</TableCell>
    </TableRow>
  );
};
interface Result {
  tick: number;
  netIncome: number;
  soldiersDafted: number;
  currentSoldiers: number;
  totalGcsEarned: number;
}

interface Props {
  simState: SimState;
  gameState: StateCalculation;
  updateSimState: (field: string, value: any) => void;
}

const TimeLapseCalculations: React.FC<Props> = ({
  simState,
  gameState,
  updateSimState,
}) => {
  const [ticks, setTicks] = useState(120);
  const [draftDuration, setDraftDuration] = useState(gameState.draftTicksMin);
  const [delayDraft, setDelayDraft] = useState(true);
  const [results, setResults] = useState<Result[]>([]);

  useEffect(() => {
    setDraftDuration(gameState.draftTicksMin);
    setResults([]);
  }, [gameState]);

  const ritualSelector = (field: 'ritual', arr: Ritual[]) => {
    return (
      <FormControl fullWidth size='small'>
        <InputLabel>{field}</InputLabel>
        <Select
          labelId={`${field}-label`}
          value={simState[field]}
          label={JSON.stringify(field)}
          onChange={(e) => {
            updateSimState(field, e.target.value);
          }}
        >
          {arr.map((item, i) => (
            <MenuItem key={i} value={JSON.stringify(item)}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const handleRunSimulation = () => {
    setResults([]);
    const tempState = JSON.parse(JSON.stringify(simState));
    let totalGcs = 0;
    const resultsSet: Result[] = [];
    const draftStartTick = delayDraft ? ticks - draftDuration : 1;
    if (delayDraft) {
      tempState.draftRate = JSON.stringify(draftRates[0]);
    }
    for (let currentTick = 1; currentTick <= ticks; currentTick++) {
      if (currentTick == draftStartTick) {
        tempState.draftRate = JSON.stringify(draftRates[4]);
      }
      const calcs = getGameState(tempState);
      totalGcs += calcs.netIncome;
      const newResult: Result = {
        tick: currentTick,
        netIncome: calcs.netIncome,
        soldiersDafted: calcs.soldiersPerTick,
        currentSoldiers: tempState.soldiers + calcs.soldiersPerTick,
        totalGcsEarned: totalGcs,
      };
      resultsSet.push(newResult);
      tempState.soldiers = newResult.currentSoldiers;
    }
    const reversed = [...resultsSet].reverse();
    setResults(reversed);
  };

  return (
    <Box mx={2}>
      <Grid container spacing={2} mt={2}>
        <Grid item sm={2} lg={1}>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='Duration'
            value={ticks}
            InputProps={{ inputProps: { min: 1, max: maxDuration } }}
            onChange={(e) => setTicks(Number(e.target.value))}
          />
        </Grid>
        <Grid item sm={2} lg={1}>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='Draft Duration'
            value={draftDuration}
            InputProps={{ inputProps: { min: 0, max: maxDuration } }}
            onChange={(e) => setDraftDuration(Number(e.target.value))}
          />
        </Grid>
        <Grid item sm={3} lg={2}>
          {ritualSelector('ritual', rituals)}
        </Grid>
        <Grid item sm={2} lg={1}>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='RitualEffect'
            value={simState['ritEff']}
            InputProps={{ inputProps: { min: 50, max: 200 } }}
            onChange={(e) =>
              updateSimState('ritEff', parseFloat(e.target.value))
            }
          />
        </Grid>
        <Grid item sm={2} lg={1}>
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={delayDraft} />}
              label='Delay draft start'
              onChange={() => setDelayDraft(!delayDraft)}
            />
          </FormGroup>
        </Grid>

        <Box mb={2} ml={2}>
          <Button
            variant='outlined'
            size='medium'
            onClick={handleRunSimulation}
            sx={{ mt: 2 }}
          >
            Run Simulation
          </Button>
        </Box>
      </Grid>
      {results.length > 0 && (
        <>
          <Box mt={2}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant='body2' gutterBottom>
                You ended up with {results[0].currentSoldiers} soldiers.
              </Typography>
              <Typography variant='body2' gutterBottom>
                You ended up with {results[0].totalGcsEarned} GCs.
              </Typography>
            </Paper>
          </Box>
          <Box mb={2}>
            <TableContainer component={Paper}>
              <Table size='small' aria-label='a dense table'>
                <TableHead>
                  <TableRow>
                    <TableCell>Tick</TableCell>
                    <TableCell align='right'>NetIncome</TableCell>
                    <TableCell align='right'>SoldiersDafted</TableCell>
                    <TableCell align='right'>CurrentSoldiers</TableCell>
                    <TableCell align='right'>TotalGcsEarned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{results.map((r) => getResult(r))}</TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TimeLapseCalculations;
